import {
  EnvironmentProviders,
  inject,
  isDevMode,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { forkJoin, catchError, of } from 'rxjs';
import {
  APP_LANGUAGES,
  DEFAULT_LANGUAGE,
  TRANSLATION_SCOPES,
} from '@core/constants/language.constants';
import { LanguageService } from '@core/services/language.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslocoHttpLoader } from './transloco-http.loader';
import { TranslocoPaginatorIntl } from './transloco-paginator-intl';

/**
 * Root i18n wiring. Added once in AppModule's providers.
 */
export function provideAppTransloco(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideTransloco({
      config: {
        availableLangs: APP_LANGUAGES.map((lang) => lang.id),
        defaultLang: DEFAULT_LANGUAGE,

        // If a key is missing from es.json, render the English string rather
        // than an empty element. Partial translation files stay shippable,
        // which is what lets a feature ship ahead of its translations.
        fallbackLang: DEFAULT_LANGUAGE,
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: isDevMode(),
        },

        // Required for switching language without a page reload. Transloco
        // defaults this to false, which would leave already-rendered text stale.
        reRenderOnLangChange: true,

        /*
         * MUST stay false. Transloco defaults it to true, which silently
         * re-prefixes any key looked up while the active lang is scoped.
         *
         * Our convention is that every key is explicit: the structural
         * directive supplies the scope via `read: 'user'`, and TypeScript
         * callers pass the fully-qualified key. With auto-prefixing on, a
         * template inside a scoped module resolves `t('login.title')` to
         * `public.public.login.title`, and a root key used in that same module
         * resolves to `public.common.showPassword` — both miss, and the raw key
         * string renders on screen. The compiler cannot catch this; it is a
         * pure runtime resolution rule.
         */
        scopes: { autoPrefixKeys: false },

        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),

    // Material's paginator builds "1 – 10 of 100" in English; this translates it app-wide.
    { provide: MatPaginatorIntl, useClass: TranslocoPaginatorIntl },

    provideAppInitializer(() => {
      const languageService = inject(LanguageService);
      const transloco = inject(TranslocoService);

      // Resolve the saved language before the first render, so the user never
      // sees a flash of English before their preference kicks in.
      languageService.init();
      const language = languageService.activeId();

      /*
       * Preload the root translations AND every scope before bootstrap.
       *
       * This is load-bearing, not an optimization. `*transloco` is a structural
       * directive: it renders its content only once the relevant translation
       * file has resolved. Roughly three dozen components in this app read
       * `@ViewChild(MatPaginator)` (and MatSort) inside `ngAfterViewInit` and
       * assign it to `dataSource.paginator`. If the translation were still in
       * flight at that moment the embedded view would not exist yet, the query
       * would be undefined, and pagination would silently break on first visit
       * to those screens.
       *
       * With the files already in Transloco's cache, the directive renders
       * synchronously during the first change-detection pass, so view queries
       * resolve exactly as they did before i18n was introduced.
       *
       * Cost is one parallel batch of small static JSON files, browser-cached
       * thereafter. If these files ever grow large enough to matter, the way to
       * reclaim laziness is to convert the affected components to setter-based
       * view queries first — not to remove this preload.
       */
      return forkJoin(
        [language, ...TRANSLATION_SCOPES.map((scope) => `${scope}/${language}`)].map((path) =>
          transloco.load(path).pipe(
            // A missing or malformed scope file must not white-screen the app.
            // Transloco falls back to English (or the raw key) for that scope.
            catchError(() => of(null))
          )
        )
      );
    }),
  ]);
}
